export interface InjectionToken<T> {
  readonly symbol: symbol;
  readonly description: string;
  readonly __type?: T;
}

export function createToken<T>(description: string): InjectionToken<T> {
  return {
    symbol: Symbol(description),
    description,
  };
}

export type Lifetime = 'singleton' | 'transient' | 'scoped';

export interface Registration<T> {
  factory: (container: Container) => T;
  lifetime: Lifetime;
}

export interface Container {
  register<T>(
    token: InjectionToken<T>,
    factory: (container: Container) => T,
    lifetime?: Lifetime,
  ): void;
  resolve<T>(token: InjectionToken<T>): T;
  createScope(): Container;
  has<T>(token: InjectionToken<T>): boolean;
}

export class DIContainer implements Container {
  private readonly registrations = new Map<symbol, Registration<unknown>>();
  private readonly singletonInstances = new Map<symbol, unknown>();
  private readonly scopedInstances = new Map<symbol, unknown>();
  private readonly parent: DIContainer | null;
  private readonly resolutionStack = new Set<symbol>();

  constructor(parent: DIContainer | null = null) {
    this.parent = parent;
  }

  register<T>(
    token: InjectionToken<T>,
    factory: (container: Container) => T,
    lifetime: Lifetime = 'singleton',
  ): void {
    this.registrations.set(token.symbol, { factory, lifetime });
  }

  resolve<T>(token: InjectionToken<T>): T {
    if (this.resolutionStack.has(token.symbol)) {
      throw new Error(
        `Circular dependency detected while resolving: ${token.description}`,
      );
    }

    // Check scoped instances first (only in scoped containers)
    if (this.parent !== null && this.scopedInstances.has(token.symbol)) {
      return this.scopedInstances.get(token.symbol) as T;
    }

    // Check singleton instances in root container
    const root = this.getRoot();
    if (root.singletonInstances.has(token.symbol)) {
      return root.singletonInstances.get(token.symbol) as T;
    }

    const registration = this.getRegistration<T>(token);
    if (!registration) {
      throw new Error(`No registration found for token: ${token.description}`);
    }

    this.resolutionStack.add(token.symbol);
    try {
      const instance = registration.factory(this) as T;

      switch (registration.lifetime) {
        case 'singleton':
          root.singletonInstances.set(token.symbol, instance);
          break;
        case 'scoped':
          if (this.parent === null) {
            throw new Error(
              `Cannot resolve scoped registration "${token.description}" from root container. Use createScope() first.`,
            );
          }
          this.scopedInstances.set(token.symbol, instance);
          break;
        case 'transient':
          // No caching for transient
          break;
      }

      return instance;
    } finally {
      this.resolutionStack.delete(token.symbol);
    }
  }

  createScope(): Container {
    const scope = new DIContainer(this);
    return scope;
  }

  has<T>(token: InjectionToken<T>): boolean {
    return this.getRegistration(token) !== undefined;
  }

  private getRegistration<T>(
    token: InjectionToken<T>,
  ): Registration<unknown> | undefined {
    const local = this.registrations.get(token.symbol);
    if (local) return local;
    if (this.parent) return this.parent.getRegistration(token);
    return undefined;
  }

  private getRoot(): DIContainer {
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- loop variable walks the parent chain, not a `this` capture
    let current: DIContainer = this;
    while (current.parent !== null) {
      current = current.parent;
    }
    return current;
  }
}

export function createContainer(): Container {
  return new DIContainer();
}
